import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import LocationDialog from "./LocationDialog";
import { resolveManualZone } from "./manualZone";
import { createGeolocationRequester, readGeolocationPermission } from "./locationPermission";
import { LOCATION_COPY } from "./locationMessages";
import { hasUsableLocation, LOCATION_STATES, publicPosition } from "./locationState";

const LocationContext = createContext(null);
export const LOCATION_DEFERRED_SESSION_KEY = "venaqqi_location_deferred_v1";
export const LOCATION_MANUAL_ZONE_KEY = "venaqqi_zona_manual";

const readStoredValue = (storage, key) => {
  try { return String(storage?.getItem(key) || "").trim(); } catch { return ""; }
};
const writeStoredValue = (storage, key, value) => {
  try {
    if (value) storage?.setItem(key, value);
    else storage?.removeItem(key);
  } catch { /* storage may be unavailable */ }
};

const initialLocationState = () => {
  const manualZone = typeof window !== "undefined" ? readStoredValue(window.localStorage, LOCATION_MANUAL_ZONE_KEY).slice(0, 160) : "";
  const deferred = typeof window !== "undefined" && readStoredValue(window.sessionStorage, LOCATION_DEFERRED_SESSION_KEY) === "1";
  return {
    permissionState: LOCATION_STATES.CHECKING,
    coordinates: null,
    coordinatesObtainedAt: null,
    accuracy: null,
    isRequestInFlight: false,
    startupExplanationShown: deferred || !!manualZone,
    continuedWithoutLocation: deferred || !!manualZone,
    manualZone: manualZone || null,
    manualCoordinates: null,
    pendingLocationAction: null,
    lastError: null,
  };
};

export function LocationProvider({ children, manualZoneResolver = resolveManualZone }) {
  const [state, setState] = useState(initialLocationState);
  const [dialog, setDialog] = useState(null);
  const [toast, setToast] = useState(null);
  const requester = useRef(createGeolocationRequester());
  const callbacks = useRef(null);
  const permissionStatus = useRef(null);
  const reconciliationInFlight = useRef(false);
  const previousPermissionState = useRef(state.permissionState);
  const permissionToastTimer = useRef(null);

  const updatePermission = useCallback(async () => {
    const permissionState = await readGeolocationPermission();
    setState(current => ({ ...current, permissionState }));
    return permissionState;
  }, []);

  const clearPending = useCallback(() => {
    callbacks.current = null;
    setState(current => ({ ...current, pendingLocationAction: null }));
  }, []);

  const obtain = useCallback(async () => {
    setState(current => ({ ...current, isRequestInFlight: true, lastError: null }));
    const result = await requester.current();
    if (result.ok) {
      const position = publicPosition(result.position);
      const resume = callbacks.current?.onGranted;
      writeStoredValue(window.localStorage, LOCATION_MANUAL_ZONE_KEY, "");
      writeStoredValue(window.sessionStorage, LOCATION_DEFERRED_SESSION_KEY, "");
      setState(current => ({ ...current, permissionState: LOCATION_STATES.GRANTED, coordinates: position.coords, coordinatesObtainedAt: position.timestamp, accuracy: position.coords.accuracy, isRequestInFlight: false, continuedWithoutLocation: false, manualZone: null, manualCoordinates: null, pendingLocationAction: null, lastError: null }));
      setDialog(null); callbacks.current = null; resume?.(position);
      return position;
    }
    setState(current => ({ ...current, permissionState: result.kind === "timeout" ? LOCATION_STATES.ERROR : result.kind, isRequestInFlight: false, lastError: result.message }));
    setDialog(current => ({ ...(current || { kind: "contextual", feature: "recommendation" }), mode: result.kind === LOCATION_STATES.DENIED ? "denied" : "error", error: result.message }));
    return null;
  }, []);

  useEffect(() => {
    const previous = previousPermissionState.current;
    previousPermissionState.current = state.permissionState;
    if (state.permissionState !== LOCATION_STATES.GRANTED || previous === LOCATION_STATES.GRANTED) return undefined;
    if (permissionToastTimer.current) clearTimeout(permissionToastTimer.current);
    setToast("📍 Ubicación habilitada");
    permissionToastTimer.current = setTimeout(() => {
      setToast(current => current === "📍 Ubicación habilitada" ? null : current);
      permissionToastTimer.current = null;
    }, 1500);
    return undefined;
  }, [state.permissionState]);

  useEffect(() => () => {
    if (permissionToastTimer.current) clearTimeout(permissionToastTimer.current);
  }, []);

  const reconcileNativeReturn = useCallback(async () => {
    if (reconciliationInFlight.current) return;
    reconciliationInFlight.current = true;
    try {
      const permission = await updatePermission();
      if (permission === LOCATION_STATES.GRANTED && callbacks.current) {
        await obtain();
      } else if (permission === LOCATION_STATES.DENIED && callbacks.current) {
        setDialog(current => ({ ...(current || { kind: "contextual", feature: "recommendation" }), mode: "denied", error: null }));
      }
    } finally {
      reconciliationInFlight.current = false;
    }
  }, [obtain, updatePermission]);

  useEffect(() => {
    if (!state.startupExplanationShown && !state.pendingLocationAction) return undefined;
    let active = true;
    const subscribe = async () => {
      if (!navigator.permissions?.query) return;
      try {
        const status = await navigator.permissions.query({ name: "geolocation" });
        if (!active) return;
        permissionStatus.current = status;
        status.onchange = reconcileNativeReturn;
      } catch { /* focus and visibility remain available as fallbacks */ }
    };
    const onVisible = () => { if (!document.hidden) reconcileNativeReturn(); };
    subscribe();
    window.addEventListener("focus", reconcileNativeReturn);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      if (permissionStatus.current) permissionStatus.current.onchange = null;
      permissionStatus.current = null;
      window.removeEventListener("focus", reconcileNativeReturn);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reconcileNativeReturn, state.pendingLocationAction, state.startupExplanationShown]);

  const requestLocationForFeature = useCallback(async (feature, action, handlers = {}) => {
    if (hasUsableLocation(state, feature)) { handlers.onGranted?.({ coords: state.coordinates, timestamp: state.coordinatesObtainedAt }); return true; }
    if (state.manualZone && !action?.forceGps) {
      handlers.onManual?.(state.manualZone, { zone: state.manualZone, coords: state.manualCoordinates });
      return true;
    }
    const descriptor = { ...action, createdAt: action?.createdAt || Date.now() };
    callbacks.current = handlers;
    setState(current => ({ ...current, pendingLocationAction: descriptor }));
    const permission = await updatePermission();
    if (permission === LOCATION_STATES.GRANTED) { await obtain(); return true; }
    setDialog({ kind: "contextual", feature, mode: permission === LOCATION_STATES.DENIED ? "denied" : "explain" });
    return false;
  }, [obtain, state, updatePermission]);

  const initializeStartup = useCallback(async () => {
    if (state.startupExplanationShown || state.continuedWithoutLocation || state.manualZone) return;
    const permission = await updatePermission();
    setState(current => ({ ...current, startupExplanationShown: true }));
    if (permission !== LOCATION_STATES.GRANTED) setDialog({ kind: "startup", feature: "startup", mode: permission === LOCATION_STATES.DENIED ? "denied" : "explain" });
  }, [state.continuedWithoutLocation, state.manualZone, state.startupExplanationShown, updatePermission]);

  const deferLocationForSession = useCallback(() => {
    writeStoredValue(window.sessionStorage, LOCATION_DEFERRED_SESSION_KEY, "1");
    setDialog(null);
    setState(current => ({ ...current, startupExplanationShown: true, continuedWithoutLocation: true, isRequestInFlight: false }));
    clearPending();
  }, [clearPending]);
  const continueWithoutLocation = useCallback(() => {
    deferLocationForSession();
    setToast(LOCATION_COPY.continued); setTimeout(() => setToast(null), 3500);
  }, [deferLocationForSession]);
  const useManual = useCallback(async zone => {
    const resume = callbacks.current?.onManual;
    setState(current => ({ ...current, isRequestInFlight: true, lastError: null }));
    setDialog(current => current ? { ...current, error: null } : current);
    let result;
    try {
      result = await manualZoneResolver(zone);
    } catch (resolverError) {
      result = { ok: false, error: resolverError?.message || "No pudimos validar esa zona. Revisa tu conexión e inténtalo de nuevo." };
    }
    if (!result?.ok) {
      const error = result?.error || "No pudimos validar esa zona.";
      setState(current => ({ ...current, isRequestInFlight: false, lastError: error }));
      setDialog(current => ({ ...(current || { kind: "contextual", feature: "recommendation", mode: "explain" }), error }));
      return false;
    }
    writeStoredValue(window.localStorage, LOCATION_MANUAL_ZONE_KEY, result.zone);
    writeStoredValue(window.localStorage, "venaqqi_gps", "0");
    writeStoredValue(window.sessionStorage, LOCATION_DEFERRED_SESSION_KEY, "1");
    setState(current => ({ ...current, manualZone: result.zone, manualCoordinates: result.coords || null, continuedWithoutLocation: true, startupExplanationShown: true, isRequestInFlight: false, pendingLocationAction: null, lastError: null }));
    setDialog(null);
    setToast(`Usaremos ${result.zone} como zona aproximada.`);
    setTimeout(() => setToast(null), 3500);
    callbacks.current = null;
    resume?.(result.zone, result);
    return true;
  }, [manualZoneResolver]);
  const cancel = useCallback(() => { const cancelled = callbacks.current?.onCancel; setDialog(null); clearPending(); cancelled?.(); }, [clearPending]);
  const dismissLocationDialog = useCallback(() => {
    if (!dialog) return;
    if (dialog.kind === "startup") continueWithoutLocation();
    else cancel();
  }, [cancel, continueWithoutLocation, dialog]);
  const clearSessionLocation = useCallback(() => {
    callbacks.current = null;
    setDialog(null);
    setState(current => ({ ...current, coordinates: null, coordinatesObtainedAt: null, accuracy: null, pendingLocationAction: null, isRequestInFlight: false }));
  }, []);

  const value = useMemo(() => ({ ...state, initializeStartup, deferLocationForSession, requestLocationForFeature, requestLocation: handlers => requestLocationForFeature("recommendation", { type: "NEARBY_SEARCH", createdAt: Date.now(), supportsManualLocation: true }, handlers), hasUsableLocation: feature => hasUsableLocation(state, feature), getLocationForFeature: feature => hasUsableLocation(state, feature) ? { coords: state.coordinates, timestamp: state.coordinatesObtainedAt } : state.manualZone ? { zone: state.manualZone, coords: state.manualCoordinates } : null, clearSessionLocation, isLocationDialogOpen: !!dialog, dismissLocationDialog }), [clearSessionLocation, deferLocationForSession, dialog, dismissLocationDialog, initializeStartup, requestLocationForFeature, state]);
  return <LocationContext.Provider value={value}>{children}<LocationDialog dialog={dialog} isRequestInFlight={state.isRequestInFlight} onAllow={obtain} onContinue={continueWithoutLocation} onManual={useManual} onCancel={cancel} />{toast && <div role="status" style={{ position: "fixed", zIndex: 15001, bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#191919", color: "#fff", border: "1px solid #e53935", borderRadius: 9, padding: "6px 8px", width: "128px", maxWidth: "calc(100% - 32px)", fontSize: 11, lineHeight: 1.25, textAlign: "center", boxShadow: "0 8px 24px rgba(0,0,0,.45)" }}>{toast}</div>}</LocationContext.Provider>;
}

export const useLocationAccess = () => {
  const value = useContext(LocationContext);
  if (!value) throw new Error("useLocationAccess must be used within LocationProvider");
  return value;
};
